import styled from "@emotion/styled";
import { useAtom } from "jotai";
import { useCallback, useEffect, useMemo } from "react";

import {
  CanvasElement,
  Droppable,
  FONT_WEIGHT_BOLD,
  getCanvasBackgroundCss,
  GridSplit,
  transientOptions,
  useViewBar,
} from "@skilitics/designer-ui-components";
import { useFormatMessage } from "@skilitics/localization";

import { useResetAspect } from "../../selection";
import { useStickyState } from "../../state";
import { CONDITION_TYPE, TYPE_ROOT_DROPPABLE } from "../constants";
import { type DroppableData } from "../dnd/dndSchemas";
import { ConditionDesigner } from "../editors";
import { ConditionPlaceholderDropzone, ResponseZoneContainer, ResponseZoneHeader } from "../editors/shared";
import { SortableEffectsList } from "../editors/SortableEffectsList";
import { useResponseDesigner } from "../ResponseByIdContext";
import { layersPanelWidthAtom } from "./atoms";
import { ResponseEditorViewBar } from "./ResponseEditorViewBar";
import { ResponseSidebar } from "./ResponseSidebar";

const NO_CONDITION_DROPZONE_ID = "NO_CONDITION_ID";

type Orientation = "horizontal" | "vertical";

const ResponseEditorStyler = styled("div", transientOptions)<{ $panelWidth: string }>`
  position: relative;
  display: grid;
  grid-template-columns: ${({ $panelWidth }) => `${$panelWidth} auto`};
  height: 100%;
  font-size: ${({ theme }) => theme.fontSize};

  ${getCanvasBackgroundCss}
  * {
    user-select: none;
  }
`;

const ScrollContainer = styled.div`
  overflow: auto;
`;

const Stage = styled.div<{ orientation: Orientation }>`
  display: grid;
  grid-gap: 16px;
  padding: 16px;
  min-width: 600px;
  min-height: 100%;

  ${({ orientation }) =>
    orientation === "vertical"
      ? `
      grid-template-rows: 1fr 1fr;
      max-width: 900px;
      margin: 0 auto;
      gap: 40px;
    `
      : `
      grid-template-columns: 1fr 1fr;
    `};
`;

const ZoneWrapper = styled.div`
  position: relative;
  height: 100%;

  ${CanvasElement} {
    max-width: 300px;
    margin: 15px auto;
  }
`;

const HeaderPrefix = styled.span`
  font-weight: ${FONT_WEIGHT_BOLD};
  color: ${({ theme }) => theme.secondary};
  margin-right: 4px;
`;

type Props = {
  responseId: number;
};

export const ResponseEditor = ({ responseId }: Props) => {
  const formatMessage = useFormatMessage(formatStrings);
  const resetAspect = useResetAspect();

  const {
    getRootCondition,
    getEffects,
    removeEffect,
    resolveAddableEffectBuildables,
    resolveAddableConditionBuildables,
  } = useResponseDesigner();

  const [layersPanelWidth, setLayersPanelWidth] = useAtom(layersPanelWidthAtom);

  const effects = useMemo(() => getEffects(), [getEffects]);
  const effectBuildables = useMemo(() => resolveAddableEffectBuildables(), [resolveAddableEffectBuildables]);
  const conditionBuildables = useMemo(() => resolveAddableConditionBuildables(), [resolveAddableConditionBuildables]);

  const [orientation, setOrientation] = useStickyState<Orientation>(`responseEditor_orientation`, "vertical");

  const onClose = useCallback(() => resetAspect(), [resetAspect]);

  const handleRemoveEffect = useCallback((effectId: number) => removeEffect(effectId), [removeEffect]);

  const rootCondition = useMemo(() => getRootCondition(), [getRootCondition]);

  const changeOrientation = useCallback(
    () => setOrientation(orientation === "vertical" ? "horizontal" : "vertical"),
    [orientation, setOrientation],
  );

  useViewBar(
    useCallback(
      () => ({
        id: "response-editor-tool-bar",
        key: `${responseId}`,
        component: (
          <ResponseEditorViewBar onClose={onClose} onOrientationChange={changeOrientation} orientation={orientation} />
        ),
      }),
      [changeOrientation, onClose, orientation, responseId],
    ),
  );

  useEffect(() => {
    const preventEscPropagation = (event: KeyboardEvent) => {
      // Pressing esc will generally deselect the currently selected node (unmounting the response editor).
      // See RootNodeDesigner for keypress handler.
      if (event.key === "Escape") {
        event.stopImmediatePropagation();
      }
    };

    window.addEventListener("keydown", preventEscPropagation, { capture: true });
    return () => window.removeEventListener("keydown", preventEscPropagation, { capture: true });
  }, []);

  return (
    <>
      <ResponseEditorStyler $panelWidth={layersPanelWidth} data-test="response-editor-container">
        <ResponseSidebar effectBuildables={effectBuildables} conditionBuildables={conditionBuildables} />

        <ScrollContainer>
          <Stage orientation={orientation} data-test="stage-container">
            <ZoneWrapper key="conditions" data-test="conditions-container">
              {rootCondition ? (
                <ConditionDesigner condition={rootCondition} />
              ) : (
                <ResponseZoneContainer>
                  <ResponseZoneHeader>
                    <HeaderPrefix>{formatMessage("headerPrefix")}</HeaderPrefix>
                    {formatMessage("noConditionsZoneHeader")}
                  </ResponseZoneHeader>
                  <Droppable
                    id={NO_CONDITION_DROPZONE_ID}
                    data={{ rule: { ruleType: CONDITION_TYPE, type: TYPE_ROOT_DROPPABLE } } satisfies DroppableData}
                    accepts={{ "rule.ruleType": [CONDITION_TYPE] }}
                    color="secondary"
                  >
                    <ConditionPlaceholderDropzone>{formatMessage("noConditionsMessage")}</ConditionPlaceholderDropzone>
                  </Droppable>
                </ResponseZoneContainer>
              )}
            </ZoneWrapper>
            <ZoneWrapper key="effects" data-test="effects-container">
              <SortableEffectsList effects={effects} onRemoveEffect={handleRemoveEffect} />
            </ZoneWrapper>
          </Stage>
        </ScrollContainer>
      </ResponseEditorStyler>

      <GridSplit minWidth={160} setWidth={setLayersPanelWidth} width={layersPanelWidth} />
    </>
  );
};

const formatStrings = {
  noConditionsMessage: {
    en: "Drag conditions here",
  },
  headerPrefix: {
    en: "When",
  },
  noConditionsZoneHeader: {
    en: "the following conditions are met:",
  },
};
